import { useCallback, useEffect, useMemo, useState } from 'react';

import API_ENDPOINTS from '@api/endpoints';
import HTTP_METHODS_ENUM from '@api/enums/HTTP_METHODS_ENUM.enum';
import httpClient from '@api/httpClient';

import { showToast } from '@app/providers/ToastProvider';

interface UseFetchInitialCredsReturn {
	fetchCreds: () => Promise<void>;
	initialStringCode?: string;
	isInitialLoading: boolean;
	initialQrCode: string;
}

function useFetchInitialCreds(): UseFetchInitialCredsReturn {
	const [initialStringCode, setInitialStringCode] = useState<string>();
	const [data, setData] = useState<ArrayBuffer>();
	const [loading, setLoading] = useState(true);

	const fetchCreds = useCallback(async () => {
		setLoading(true);
		try {
			const { data, headers } = await httpClient.request<ArrayBuffer>({
				url: API_ENDPOINTS.MFA.TOTP.GET_INITIAL_CREDS(),
				method: HTTP_METHODS_ENUM.POST,
				data: { digits: 6, name: 'Onboarding' },
				responseType: 'arraybuffer',
			});
			setData(data);
			setInitialStringCode(headers['x-totp-secret']);
		} catch (e) {
			showToast.error('Oops. Something went wrong.');
			setData(undefined);
			setInitialStringCode(undefined);
		} finally {
			setLoading(false);
		}
	}, []);

	const transformArrayBufferToImgSrc = useMemo(() => {
		if (data) {
			const blob = new Blob([data], { type: 'image/jpeg' });
			const urlCreator = window.URL || window.webkitURL;
			return urlCreator.createObjectURL(blob);
		} else return '';
	}, [data]);

	useEffect(() => {
		fetchCreds();
	}, [fetchCreds]);

	useEffect(() => {
		return () => {
			if (transformArrayBufferToImgSrc) {
				const urlCreator = window.URL || window.webkitURL;
				urlCreator.revokeObjectURL(transformArrayBufferToImgSrc);
			}
		};
	}, [transformArrayBufferToImgSrc]);

	return {
		initialQrCode: transformArrayBufferToImgSrc,
		isInitialLoading: loading,
		initialStringCode,
		fetchCreds,
	};
}

export default useFetchInitialCreds;
